import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import apiClient from '../../../shared/api/apiClient';
import './styles.css';

const AttributeDataType = {
    Text: 1,
    Number: 2,
    Boolean: 3,
    Date: 4,
    List: 5
} as const;

type AttributeDataType = typeof AttributeDataType[keyof typeof AttributeDataType];

const AttributeFilterMode = {
    ValueList: 1,
    NumericRange: 2,
    BooleanCheckbox: 3
} as const;

type AttributeFilterMode = typeof AttributeFilterMode[keyof typeof AttributeFilterMode];

interface CategoryAvailableAttributeDto {
    id: number;
    code: string;
    name: string;
    dataType: AttributeDataType;
    dataTypeName: string;
    groupName?: string;
    unitName?: string;
    unitSymbol?: string;
    isFilterable: boolean;
    sortOrder: number;
}

interface CategoryAttributeSettingDto {
    id: number;
    clientCategoryId: number;
    attributeDefinitionId: number;
    attributeCode: string;
    attributeName: string;
    dataType: AttributeDataType;
    dataTypeName: string;
    unitName?: string;
    unitSymbol?: string;
    sortOrder: number;
    isVisible: boolean;
    isFilterable: boolean;
    isRequired: boolean;
    isExportedToB2B: boolean;
    localName?: string;
    filterMode: AttributeFilterMode;
}

interface CategoryAttributeSettingsResponse {
    clientCategoryId: number;
    settings: CategoryAttributeSettingDto[];
    availableAttributes: CategoryAvailableAttributeDto[];
}

interface AttributeSearchResponse {
    items: CategoryAvailableAttributeDto[];
    hasMore: boolean;
}

interface CategoryAttributeSettingsProps {
    categoryId?: number;
    rootCategoryId?: number;
    canEdit: boolean;
}

type AttributeSettingRow = CategoryAttributeSettingDto;

const filterModeLabels: Record<AttributeFilterMode, string> = {
    [AttributeFilterMode.ValueList]: 'Список',
    [AttributeFilterMode.NumericRange]: 'Диапазон',
    [AttributeFilterMode.BooleanCheckbox]: 'Да/нет'
};

const getDefaultFilterMode = (dataType: AttributeDataType): AttributeFilterMode => {
    if (dataType === AttributeDataType.Number) {
        return AttributeFilterMode.NumericRange;
    }

    if (dataType === AttributeDataType.Boolean) {
        return AttributeFilterMode.BooleanCheckbox;
    }

    return AttributeFilterMode.ValueList;
};

const CategoryAttributeSettings: React.FC<CategoryAttributeSettingsProps> = ({
    categoryId,
    rootCategoryId,
    canEdit
}) => {
    const [settings, setSettings] = useState<AttributeSettingRow[]>([]);
    const [attributeSearchQuery, setAttributeSearchQuery] = useState('');
    const [attributeSearchResults, setAttributeSearchResults] = useState<CategoryAvailableAttributeDto[]>([]);
    const [selectedAttribute, setSelectedAttribute] = useState<CategoryAvailableAttributeDto | null>(null);
    const [attributeSearchPage, setAttributeSearchPage] = useState(1);
    const [attributeSearchHasMore, setAttributeSearchHasMore] = useState(false);
    const [attributeSearchOpen, setAttributeSearchOpen] = useState(false);
    const [attributeSearchLoading, setAttributeSearchLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const searchRequestId = useRef(0);
    const searchContainerRef = useRef<HTMLDivElement | null>(null);

    const isSelectableCategory = Boolean(categoryId && categoryId !== rootCategoryId);

    const loadSettings = useCallback(async () => {
        if (!categoryId || categoryId === rootCategoryId) {
            setSettings([]);
            setAttributeSearchResults([]);
            setSelectedAttribute(null);
            setAttributeSearchQuery('');
            return;
        }

        setLoading(true);
        try {
            const response = await apiClient.get<CategoryAttributeSettingsResponse>(
                `api/client-categories/${categoryId}/attributes`);

            setSettings(response.data.settings ?? []);
        } catch (error) {
            console.error('Error loading category attributes:', error);
        } finally {
            setLoading(false);
        }
    }, [categoryId, rootCategoryId]);

    useEffect(() => {
        void loadSettings();
    }, [loadSettings]);

    const assignedAttributeIds = useMemo(
        () => new Set(settings.map(setting => setting.attributeDefinitionId)),
        [settings]);

    const unassignedSearchResults = useMemo(
        () => attributeSearchResults.filter(attribute => !assignedAttributeIds.has(attribute.id)),
        [attributeSearchResults, assignedAttributeIds]);

    const loadAttributeSearchPage = useCallback(async (query: string, page: number, append: boolean) => {
        const trimmedQuery = query.trim();
        if (trimmedQuery.length < 2) {
            setAttributeSearchResults([]);
            setAttributeSearchHasMore(false);
            setAttributeSearchLoading(false);
            return;
        }

        const requestId = ++searchRequestId.current;
        setAttributeSearchLoading(true);

        try {
            const response = await apiClient.get<AttributeSearchResponse>(
                `api/attributes/search?query=${encodeURIComponent(trimmedQuery)}&page=${page}&pageSize=20`,
                {},
                false);

            if (requestId !== searchRequestId.current) {
                return;
            }

            setAttributeSearchResults(current => append
                ? [...current, ...(response.data.items ?? [])]
                : response.data.items ?? []);
            setAttributeSearchHasMore(response.data.hasMore);
            setAttributeSearchPage(page);
        } catch (error) {
            if (requestId === searchRequestId.current) {
                console.error('Error searching attributes:', error);
                setAttributeSearchResults([]);
                setAttributeSearchHasMore(false);
            }
        } finally {
            if (requestId === searchRequestId.current) {
                setAttributeSearchLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        const trimmedQuery = attributeSearchQuery.trim();
        setSelectedAttribute(current => current && current.name === attributeSearchQuery ? current : null);

        if (!attributeSearchOpen || trimmedQuery.length < 2) {
            setAttributeSearchResults([]);
            setAttributeSearchHasMore(false);
            setAttributeSearchLoading(false);
            searchRequestId.current += 1;
            return;
        }

        const timeoutId = window.setTimeout(() => {
            void loadAttributeSearchPage(trimmedQuery, 1, false);
        }, 300);

        return () => window.clearTimeout(timeoutId);
    }, [attributeSearchOpen, attributeSearchQuery, loadAttributeSearchPage]);

    useEffect(() => {
        const handlePointerDown = (event: PointerEvent) => {
            if (!searchContainerRef.current?.contains(event.target as Node)) {
                setAttributeSearchOpen(false);
            }
        };

        document.addEventListener('pointerdown', handlePointerDown);
        return () => document.removeEventListener('pointerdown', handlePointerDown);
    }, []);

    const handleAddAttribute = () => {
        if (!selectedAttribute || assignedAttributeIds.has(selectedAttribute.id)) {
            return;
        }

        setSettings(current => [
            ...current,
            {
                id: 0,
                clientCategoryId: categoryId ?? 0,
                attributeDefinitionId: selectedAttribute.id,
                attributeCode: selectedAttribute.code,
                attributeName: selectedAttribute.name,
                dataType: selectedAttribute.dataType,
                dataTypeName: selectedAttribute.dataTypeName,
                unitName: selectedAttribute.unitName,
                unitSymbol: selectedAttribute.unitSymbol,
                sortOrder: current.length === 0 ? 10 : Math.max(...current.map(x => x.sortOrder)) + 10,
                isVisible: true,
                isFilterable: selectedAttribute.isFilterable,
                isRequired: false,
                isExportedToB2B: false,
                localName: '',
                filterMode: getDefaultFilterMode(selectedAttribute.dataType)
            }
        ]);
        setSelectedAttribute(null);
        setAttributeSearchQuery('');
        setAttributeSearchResults([]);
        setAttributeSearchHasMore(false);
        setAttributeSearchOpen(false);
    };

    const updateSetting = <TKey extends keyof AttributeSettingRow>(
        attributeDefinitionId: number,
        key: TKey,
        value: AttributeSettingRow[TKey]) => {
        setSettings(current => current.map(setting =>
            setting.attributeDefinitionId === attributeDefinitionId
                ? { ...setting, [key]: value }
                : setting));
    };

    const removeSetting = (attributeDefinitionId: number) => {
        setSettings(current => current.filter(setting => setting.attributeDefinitionId !== attributeDefinitionId));
    };

    const saveSettings = async () => {
        if (!categoryId || categoryId === rootCategoryId) {
            return;
        }

        setSaving(true);
        try {
            const response = await apiClient.put<CategoryAttributeSettingsResponse>(
                `api/client-categories/${categoryId}/attributes`,
                {
                    settings: settings.map(setting => ({
                        attributeDefinitionId: setting.attributeDefinitionId,
                        sortOrder: Number(setting.sortOrder) || 0,
                        isVisible: setting.isVisible,
                        isFilterable: setting.isFilterable,
                        isRequired: setting.isRequired,
                        isExportedToB2B: setting.isExportedToB2B,
                        localName: setting.localName,
                        filterMode: Number(setting.filterMode)
                    }))
                });

            setSettings(response.data.settings ?? []);
            setSelectedAttribute(null);
            setAttributeSearchQuery('');
            setAttributeSearchResults([]);
            setAttributeSearchHasMore(false);
            toast.success('Атрибуты категории сохранены');
        } catch (error) {
            console.error('Error saving category attributes:', error);
        } finally {
            setSaving(false);
        }
    };

    if (!isSelectableCategory) {
        return (
            <div className="category-attributes-panel">
                <div className="content-placeholder">
                    <p>Выберите рабочую категорию, чтобы настроить атрибуты</p>
                </div>
            </div>
        );
    }

    return (
        <div className="category-attributes-panel">
            <div className="category-attributes-toolbar">
                <div>
                    <h3 className="section-title">Атрибуты категории</h3>
                    <span>{settings.length} назначено</span>
                </div>

                <div className="category-attributes-actions">
                    <div
                        className="category-attribute-search"
                        ref={searchContainerRef}
                    >
                        <input
                            className="form-control"
                            value={attributeSearchQuery}
                            onChange={(event) => {
                                setAttributeSearchQuery(event.target.value);
                                setAttributeSearchOpen(true);
                            }}
                            onFocus={() => setAttributeSearchOpen(true)}
                            placeholder="Введите минимум 2 символа"
                            disabled={!canEdit || loading}
                        />
                        {attributeSearchOpen && canEdit && !loading && (
                            <div className="category-attribute-search-menu">
                                {attributeSearchQuery.trim().length < 2 ? (
                                    <div className="category-attribute-search-status">
                                        Начните вводить название или код атрибута
                                    </div>
                                ) : unassignedSearchResults.length === 0 && !attributeSearchLoading ? (
                                    <div className="category-attribute-search-status">
                                        Атрибуты не найдены
                                    </div>
                                ) : (
                                    <div
                                        className="category-attribute-search-list"
                                        onScroll={(event) => {
                                            const element = event.currentTarget;
                                            const isNearBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 24;
                                            if (isNearBottom && attributeSearchHasMore && !attributeSearchLoading) {
                                                void loadAttributeSearchPage(attributeSearchQuery, attributeSearchPage + 1, true);
                                            }
                                        }}
                                    >
                                        {unassignedSearchResults.map(attribute => (
                                            <button
                                                className="category-attribute-search-option"
                                                key={attribute.id}
                                                onClick={() => {
                                                    setSelectedAttribute(attribute);
                                                    setAttributeSearchQuery(attribute.name);
                                                    setAttributeSearchOpen(false);
                                                }}
                                                type="button"
                                            >
                                                <strong>{attribute.name}</strong>
                                                <span>
                                                    {attribute.code}
                                                    {attribute.unitSymbol ? `, ${attribute.unitSymbol}` : ''}
                                                    {attribute.groupName ? `, ${attribute.groupName}` : ''}
                                                </span>
                                            </button>
                                        ))}
                                        {attributeSearchLoading && (
                                            <div className="category-attribute-search-status">
                                                Загрузка...
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={handleAddAttribute}
                        disabled={!canEdit || !selectedAttribute || assignedAttributeIds.has(selectedAttribute.id)}
                        type="button"
                    >
                        Добавить
                    </button>
                    <button
                        className="btn btn-sm btn-primary"
                        onClick={saveSettings}
                        disabled={!canEdit || saving || loading}
                        type="button"
                    >
                        {saving ? 'Сохранение...' : 'Сохранить'}
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="content-placeholder">
                    <p>Загрузка атрибутов...</p>
                </div>
            ) : settings.length === 0 ? (
                <div className="content-placeholder">
                    <p>Для категории пока нет атрибутов</p>
                </div>
            ) : (
                <div className="category-attributes-table-wrap">
                    <table className="category-attributes-table">
                        <thead>
                            <tr>
                                <th>Атрибут</th>
                                <th>Локальное имя</th>
                                <th>Порядок</th>
                                <th>Режим</th>
                                <th>Видим.</th>
                                <th>Фильтр</th>
                                <th>Обяз.</th>
                                <th>B2B</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {settings.map(setting => (
                                <tr key={setting.attributeDefinitionId}>
                                    <td>
                                        <div className="category-attribute-name">
                                            <strong>{setting.attributeName}</strong>
                                            <span>{setting.attributeCode}{setting.unitSymbol ? `, ${setting.unitSymbol}` : ''}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <input
                                            className="form-control"
                                            value={setting.localName ?? ''}
                                            onChange={(event) => updateSetting(setting.attributeDefinitionId, 'localName', event.target.value)}
                                            disabled={!canEdit}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            className="form-control category-order-input"
                                            value={setting.sortOrder}
                                            onChange={(event) => updateSetting(setting.attributeDefinitionId, 'sortOrder', Number(event.target.value))}
                                            disabled={!canEdit}
                                        />
                                    </td>
                                    <td>
                                        <select
                                            className="form-control"
                                            value={setting.filterMode}
                                            onChange={(event) => updateSetting(setting.attributeDefinitionId, 'filterMode', Number(event.target.value) as AttributeFilterMode)}
                                            disabled={!canEdit}
                                        >
                                            {Object.entries(filterModeLabels).map(([value, label]) => (
                                                <option key={value} value={value}>{label}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td><input type="checkbox" checked={setting.isVisible} onChange={(event) => updateSetting(setting.attributeDefinitionId, 'isVisible', event.target.checked)} disabled={!canEdit} /></td>
                                    <td><input type="checkbox" checked={setting.isFilterable} onChange={(event) => updateSetting(setting.attributeDefinitionId, 'isFilterable', event.target.checked)} disabled={!canEdit} /></td>
                                    <td><input type="checkbox" checked={setting.isRequired} onChange={(event) => updateSetting(setting.attributeDefinitionId, 'isRequired', event.target.checked)} disabled={!canEdit} /></td>
                                    <td><input type="checkbox" checked={setting.isExportedToB2B} onChange={(event) => updateSetting(setting.attributeDefinitionId, 'isExportedToB2B', event.target.checked)} disabled={!canEdit} /></td>
                                    <td>
                                        <button
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={() => removeSetting(setting.attributeDefinitionId)}
                                            disabled={!canEdit}
                                            type="button"
                                        >
                                            Убрать
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default CategoryAttributeSettings;
